import { GOOGLE_API_KEY } from "../configs/env-variables";

/**
 * Loads Google Maps JavaScript API dynamically (only when needed)
 * Uses callback approach for better control and non-blocking loading
 * @returns Promise that resolves when the API is loaded
 */
export const loadGoogleMaps = (): Promise<typeof google.maps> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      resolve(window.google.maps);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );
    if (existingScript) {
      // Wait for existing script to load
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(checkInterval);
          resolve(window.google.maps);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google?.maps?.places) {
          reject(new Error("Google Maps API failed to load within timeout"));
        }
      }, 10000);
      return;
    }

    // Create unique callback name
    const callbackName = `googleMapsCallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Set up global callback
    (window as any)[callbackName] = () => {
      delete (window as any)[callbackName];
      if (window.google?.maps?.places) {
        resolve(window.google.maps);
      } else {
        reject(
          new Error("Google Maps API loaded but places library not available"),
        );
      }
    };

    // Create and load script with callback
    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&callback=${callbackName}`;
    script.onerror = () => {
      delete (window as any)[callbackName];
      reject(new Error("Failed to load Google Maps script"));
    };
    document.head.appendChild(script);
  });
};

// Type declaration for Google Maps API
declare global {
  interface Window {
    google?: {
      maps?: {
        places?: unknown;
      };
    };
  }
}
